// ./src/components/features/StaffPay/StaffPayPeopleList/StaffPayPeopleList.tsx
//
// STAFF PAY (#A4.7) — every paid person: what they earn in total, what was paid and what is still owed, and every
// shift with its status, time, rate, pay and bonus. "Mark paid" takes their FINISHED, unpaid shifts; each payment can
// be undone. Desktop: ONE table on a surface — a heading row per person (name, figures, Mark paid) over their shift
// rows and payment lines (ROADMAP #111: it was a card per person, each with its own table and header). Smaller
// screens: a card per person with the shifts stacked, and phones swipe to "Mark paid" (a do-something action — the
// left side).
//
// ⚠ Every amount is the API's — nothing here computes pay.

import React from "react";
import { Badge, Box, Button, Group, Paper, Skeleton, Stack, Table, Text, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { StaffPayPerson, StaffPayShift, STAFF_PAY_STATUS } from "@/utils/api";
import { formatEventWhenParts, HIGHLIGHT_STYLE } from "@/utils";
import { useAppEnvironment } from "@/contexts";
import { AppIcon, CardSkeletonActions, CardSkeletonLines, EmptyState, SwipeableCard, SwipeAction, useSwipeableList, countCardActions } from "@/components/generic";

import { formatPayMinutes, moneyFormatter, payableShifts, paymentsOf, staffPayStatusMeta } from "../staffPayHelpers";

export interface StaffPayPeopleListProps {
  people: StaffPayPerson[];
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  currencyCode?: string | null;
  eventTimezone?: string | null;
  /** Budget edit — Mark paid + Undo. */
  canEdit?: boolean;
  onMarkPaid?: (person: StaffPayPerson) => void;
  onUndo?: (person: StaffPayPerson, budgetPaymentId: string) => void;
  /** An Undo in flight, keyed by payment id. */
  isUndoing?: (budgetPaymentId: string) => boolean;
  /** The person last marked paid. */
  highlightedId?: string | null;
  hasQueryNarrowing?: boolean;
  /** Empty-state action: where rates are set. */
  onGoToShifts?: () => void;
}

// ============================================
// Pieces
// ============================================

const StatusBadge: React.FC<{ shift: StaffPayShift }> = ({ shift }) => {
  const meta = staffPayStatusMeta(shift.status);

  return (
    <Tooltip label={meta.hint} withArrow position="top" multiline w={260}>
      <Badge size="sm" variant="light" color={meta.color}>
        {meta.label}
      </Badge>
    </Tooltip>
  );
};

const PaidBadge: React.FC<{ shift: StaffPayShift }> = ({ shift }) => {
  const { t } = useTranslation(["volunteers"]);

  if (!shift.budgetPaymentId) {
    return null;
  }

  return (
    <Badge size="sm" variant="light" color="teal" leftSection={<AppIcon iconName="Check" size={12} />}>
      {t("volunteers:staffPay.shift.paid", { defaultValue: "Paid" })}
    </Badge>
  );
};

const BonusLine: React.FC<{ shift: StaffPayShift; money: (v?: number | null) => string }> = ({ shift, money }) => {
  const { t } = useTranslation(["volunteers"]);

  if (!shift.bonusAmount) {
    return null;
  }

  // ⚠ "+ $20 bonus" under a figure that ALREADY CONTAINS IT read as $80 + $20 = $120 (found 2026-09-19) — on
  // screen and on the timesheet a volunteer signs. The server's `total` is amount + bonus, so the line says what
  // the figure is MADE OF, never what to add to it.
  const label = t("volunteers:staffPay.shift.bonus", { defaultValue: "includes {{amount}} bonus", amount: money(shift.bonusAmount) });

  return shift.bonusNote ? (
    <Tooltip label={shift.bonusNote} withArrow position="top" multiline w={260}>
      <Text size="md" c="grape" style={{ cursor: "help" }}>
        {label}
      </Text>
    </Tooltip>
  ) : (
    <Text size="md" c="grape">
      {label}
    </Text>
  );
};

/** "7h 30m · 30m break" — the billable time, and the break taken off it. */
const HoursText: React.FC<{ shift: StaffPayShift }> = ({ shift }) => {
  const { t } = useTranslation(["volunteers"]);

  return (
    <Box>
      <Text size="md">{shift.status === STAFF_PAY_STATUS.NO_SHOW ? "—" : formatPayMinutes(shift.minutes)}</Text>
      {shift.breakMinutes ? (
        <Text size="sm" c="dimmed">
          {t("volunteers:staffPay.shift.breakTaken", { defaultValue: "{{time}} break", time: formatPayMinutes(shift.breakMinutes) })}
        </Text>
      ) : null}
    </Box>
  );
};

const ShiftWhen: React.FC<{ shift: StaffPayShift; eventTimezone?: string | null }> = ({ shift, eventTimezone }) => {
  const when = formatEventWhenParts(shift.startsAt, shift.endsAt, eventTimezone);

  if (!when) {
    return (
      <Text size="md" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Box>
      <Text size="md" style={{ whiteSpace: "nowrap" }}>
        {when.date}
      </Text>
      <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
        {when.time}
      </Text>
    </Box>
  );
};

const ShiftTitle: React.FC<{ shift: StaffPayShift }> = ({ shift }) => {
  const { t } = useTranslation(["volunteers"]);

  return (
    <Box style={{ minWidth: 0 }}>
      <Text size="md" fw={500} lineClamp={1}>
        {shift.slotName || t("volunteers:staffPay.shift.unnamed", { defaultValue: "Shift" })}
      </Text>
      {shift.roleName ? (
        <Text size="sm" c="dimmed" lineClamp={1}>
          {shift.roleName}
        </Text>
      ) : null}
    </Box>
  );
};

// Desktop table column widths — the skeleton rows use the same ones, so the swap does not move a column.
const COL = { when: 190, status: 120, hours: 120, rate: 110, pay: 160, paid: 90 };
const COLUMN_COUNT = 7;

// Desktop: the shift columns, once for every person.
const ShiftsHead: React.FC = () => {
  const { t } = useTranslation(["volunteers"]);

  return (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("volunteers:staffPay.columns.shift", { defaultValue: "Shift" })}</Table.Th>
        <Table.Th style={{ width: COL.when }}>{t("volunteers:staffPay.columns.when", { defaultValue: "When" })}</Table.Th>
        <Table.Th style={{ width: COL.status }}>{t("volunteers:staffPay.columns.status", { defaultValue: "Status" })}</Table.Th>
        <Table.Th style={{ width: COL.hours }}>{t("volunteers:staffPay.columns.hours", { defaultValue: "Time" })}</Table.Th>
        <Table.Th style={{ width: COL.rate }} ta="right">
          {t("volunteers:staffPay.columns.rate", { defaultValue: "Rate / h" })}
        </Table.Th>
        <Table.Th style={{ width: COL.pay }} ta="right">
          {t("volunteers:staffPay.columns.pay", { defaultValue: "Pay" })}
        </Table.Th>
        <Table.Th style={{ width: COL.paid }} />
      </Table.Tr>
    </Table.Thead>
  );
};

// Desktop: one shift.
const ShiftRow: React.FC<{ shift: StaffPayShift; money: (v?: number | null) => string; eventTimezone?: string | null }> = ({
  shift,
  money,
  eventTimezone,
}) => (
  <Table.Tr style={{ verticalAlign: "top" }}>
    <Table.Td>
      <ShiftTitle shift={shift} />
    </Table.Td>
    <Table.Td style={{ width: COL.when }}>
      <ShiftWhen shift={shift} eventTimezone={eventTimezone} />
    </Table.Td>
    <Table.Td style={{ width: COL.status }}>
      <StatusBadge shift={shift} />
    </Table.Td>
    <Table.Td style={{ width: COL.hours }}>
      <HoursText shift={shift} />
    </Table.Td>
    <Table.Td style={{ width: COL.rate }} ta="right">
      <Text size="md">{shift.rate != null ? money(shift.rate) : "—"}</Text>
    </Table.Td>
    <Table.Td style={{ width: COL.pay }} ta="right">
      <Text size="md" fw={600}>
        {money(shift.total)}
      </Text>
      <BonusLine shift={shift} money={money} />
    </Table.Td>
    <Table.Td style={{ width: COL.paid }}>
      <PaidBadge shift={shift} />
    </Table.Td>
  </Table.Tr>
);

// Desktop skeleton: a person's heading row over two shift rows, in the real columns.
const PersonRowsSkeleton: React.FC = () => (
  <>
    <Table.Tr style={{ backgroundColor: "var(--mantine-color-default-hover)" }}>
      <Table.Td colSpan={COLUMN_COUNT}>
        <Group justify="space-between" wrap="nowrap">
          <Box>
            <Skeleton height={20} width={180} mb={8} />
            <Skeleton height={14} width={220} />
          </Box>
          <Group gap="lg" wrap="nowrap">
            <Skeleton height={36} width={70} />
            <Skeleton height={36} width={70} />
            <Skeleton height={36} width={70} />
            <Skeleton height={36} width={130} radius="sm" />
          </Group>
        </Group>
      </Table.Td>
    </Table.Tr>
    {[0, 1].map((i) => (
      <Table.Tr key={i}>
        <Table.Td>
          <Skeleton height={16} width="50%" mb={6} />
          <Skeleton height={13} width="30%" />
        </Table.Td>
        <Table.Td style={{ width: COL.when }}>
          <Skeleton height={16} width={110} mb={6} />
          <Skeleton height={13} width={80} />
        </Table.Td>
        <Table.Td style={{ width: COL.status }}>
          <Skeleton height={20} width={70} radius="xl" />
        </Table.Td>
        <Table.Td style={{ width: COL.hours }}>
          <Skeleton height={16} width={60} />
        </Table.Td>
        <Table.Td style={{ width: COL.rate }}>
          <Skeleton height={16} width={50} ml="auto" />
        </Table.Td>
        <Table.Td style={{ width: COL.pay }}>
          <Skeleton height={16} width={70} ml="auto" />
        </Table.Td>
        <Table.Td style={{ width: COL.paid }} />
      </Table.Tr>
    ))}
  </>
);

// Tablet / phone: the same facts, stacked per shift.
const ShiftsStack: React.FC<{ shifts: StaffPayShift[]; money: (v?: number | null) => string; eventTimezone?: string | null }> = ({
  shifts,
  money,
  eventTimezone,
}) => {
  const { t } = useTranslation(["volunteers"]);

  return (
    <Stack gap="sm">
      {shifts.map((shift) => (
        <Box
          key={shift.assignmentId}
          pt="sm"
          style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
        >
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
            <ShiftTitle shift={shift} />
            <Box ta="right">
              <Text size="md" fw={600} style={{ whiteSpace: "nowrap" }}>
                {money(shift.total)}
              </Text>
              <BonusLine shift={shift} money={money} />
            </Box>
          </Group>
          <ShiftWhen shift={shift} eventTimezone={eventTimezone} />
          <Group gap={6} mt={6} wrap="wrap">
            <StatusBadge shift={shift} />
            <PaidBadge shift={shift} />
            <Text size="sm" c="dimmed">
              {[
                shift.status === STAFF_PAY_STATUS.NO_SHOW ? null : formatPayMinutes(shift.minutes),
                shift.rate != null ? t("volunteers:staffPay.shift.atRate", { defaultValue: "at {{rate}} / h", rate: money(shift.rate) }) : null,
                shift.breakMinutes
                  ? t("volunteers:staffPay.shift.breakTaken", { defaultValue: "{{time}} break", time: formatPayMinutes(shift.breakMinutes) })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          </Group>
        </Box>
      ))}
    </Stack>
  );
};

/** The person's figures: earned (expected + worked), paid, owed. */
const PersonFigures: React.FC<{ person: StaffPayPerson; money: (v?: number | null) => string }> = ({ person, money }) => {
  const { t } = useTranslation(["volunteers"]);
  const cells = [
    { key: "total", label: t("volunteers:staffPay.person.total", { defaultValue: "Earns" }), value: money(person.totals.total), color: undefined },
    { key: "paid", label: t("volunteers:staffPay.person.paid", { defaultValue: "Paid" }), value: money(person.paid), color: person.paid > 0 ? "teal" : undefined },
    { key: "owed", label: t("volunteers:staffPay.person.owed", { defaultValue: "Owed" }), value: money(person.owed), color: person.owed > 0 ? "red" : undefined },
  ];

  return (
    <Group gap="lg" wrap="nowrap">
      {cells.map((cell) => (
        <Box key={cell.key} ta="right">
          <Text size="sm" c="dimmed">
            {cell.label}
          </Text>
          <Text size="md" fw={700} c={cell.color} style={{ whiteSpace: "nowrap" }}>
            {cell.value}
          </Text>
        </Box>
      ))}
    </Group>
  );
};

/**
 * The card this list draws per person, in its own shape (#117): the name and contact, the three figures and — on a
 * tablet — the wide "Mark paid" TEXT button (the generic card skeleton drew a 28px circle for it), then a shift. A
 * person's shifts vary in number; one is drawn, the least a person on this page has.
 */
const StaffPayCardSkeleton: React.FC<{ actions: number }> = ({ actions }) => (
  <Paper p="md" withBorder radius="md">
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start" gap="md">
        <Box style={{ minWidth: 0, flex: "1 1 220px" }}>
          <CardSkeletonLines count={2} sizes={["lg", "md"]} gap={0} />
        </Box>
        <Group gap="md" align="center" wrap="wrap" justify="flex-end">
          <Group gap="lg" wrap="nowrap">
            {[0, 1, 2].map((i) => (
              <Stack key={i} gap={6} align="flex-end">
                <Skeleton height={12} width={40} />
                <Skeleton height={16} width={64} />
              </Stack>
            ))}
          </Group>
          <CardSkeletonActions count={actions} button={{ height: 36, width: 150 }} />
        </Group>
      </Group>
      <Box pt="sm" style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
        {/* A shift: its title, when, and the status / pay badges. */}
        <CardSkeletonLines count={3} gap={0} />
      </Box>
    </Stack>
  </Paper>
);

// ============================================
// Main
// ============================================

const StaffPayPeopleListComponent: React.FC<StaffPayPeopleListProps> = ({
  people,
  isLoaded,
  currencyCode,
  eventTimezone,
  canEdit = false,
  onMarkPaid,
  onUndo,
  isUndoing,
  highlightedId,
  hasQueryNarrowing = false,
  onGoToShifts,
}) => {
  const { t } = useTranslation(["volunteers", "common"]);
  const { isDesktop } = useAppEnvironment();
  const { openCard, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const money = React.useMemo(() => moneyFormatter(currencyCode), [currencyCode]);

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  if (!isLoaded && isDesktop) {
    return (
      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        <Table verticalSpacing="sm" horizontalSpacing="md" style={{ tableLayout: "fixed" }}>
          <ShiftsHead />
          <Table.Tbody>
            <PersonRowsSkeleton />
            <PersonRowsSkeleton />
          </Table.Tbody>
        </Table>
      </Paper>
    );
  }

  if (!isLoaded) {
    return (
      <Stack gap="md" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <StaffPayCardSkeleton key={i} actions={countCardActions(canEdit && onMarkPaid)} />
        ))}
      </Stack>
    );
  }

  if (people.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text ta="center" c="dimmed" py="xl">
          {t("volunteers:staffPay.noMatch", { defaultValue: "Nobody matches your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder radius="md" p="xl">
        <EmptyState
          title={t("volunteers:staffPay.empty.title", { defaultValue: "No paid shifts yet" })}
          description={t("volunteers:staffPay.empty.description", {
            defaultValue: "Give a role or a shift an hourly rate and the people who work it show up here, with what they earn.",
          })}
          iconName="Coin"
          actionLabel={onGoToShifts ? t("volunteers:staffPay.empty.action", { defaultValue: "Go to shifts" }) : undefined}
          onAction={onGoToShifts}
          size="md"
        />
      </Paper>
    );
  }

  // What one person needs on either layout: their payable shifts, payments, and the Mark paid action.
  const viewOf = (person: StaffPayPerson) => {
    const payable = payableShifts(person);
    const canMarkPaid = canEdit && !!onMarkPaid && payable.length > 0;

    return {
      id: person.eventGuestId,
      payments: paymentsOf(person),
      canMarkPaid,
      contact: [person.email, person.phone].filter(Boolean).join(" · "),
      markPaidButton: canMarkPaid ? (
        <Button
          variant="light"
          color="teal"
          leftSection={<AppIcon iconName="Cash" size={16} />}
          onClick={(e) => {
            e.stopPropagation();
            onMarkPaid?.(person);
          }}
        >
          {t("volunteers:staffPay.actions.markPaidCount", {
            defaultValue: "Mark paid ({{count}})",
            count: payable.length,
          })}
        </Button>
      ) : null,
    };
  };

  const personName = (person: StaffPayPerson) => person.name || t("volunteers:staffPay.person.unnamed", { defaultValue: "Unnamed" });

  // One payment: what it covered, and Undo.
  const paymentLine = (person: StaffPayPerson, payment: ReturnType<typeof paymentsOf>[number]) => (
    <Group key={payment.budgetPaymentId} gap="sm" justify="space-between" wrap="nowrap">
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <AppIcon iconName="Receipt" size={16} />
        <Text size="md" c="dimmed" lineClamp={1}>
          {t("volunteers:staffPay.payment.line", {
            defaultValue: "One payment for {{count}} shifts: {{shifts}}",
            count: payment.shifts.length,
            shifts: payment.shifts.map((s) => s.slotName || "—").join(", "),
          })}
        </Text>
      </Group>
      {canEdit && onUndo ? (
        <Button
          variant="subtle"
          color="gray"
          leftSection={<AppIcon iconName="ArrowBackUp" size={16} />}
          loading={isUndoing?.(payment.budgetPaymentId) ?? false}
          onClick={(e) => {
            e.stopPropagation();
            onUndo(person, payment.budgetPaymentId);
          }}
        >
          {t("volunteers:staffPay.actions.undo", { defaultValue: "Undo" })}
        </Button>
      ) : null}
    </Group>
  );

  // ── Desktop: one table, a heading row per person ──
  if (isDesktop) {
    return (
      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        <Table verticalSpacing="sm" horizontalSpacing="md" style={{ tableLayout: "fixed" }}>
          <ShiftsHead />
          <Table.Tbody>
            {people.map((person) => {
              const view = viewOf(person);

              return (
                <React.Fragment key={view.id}>
                  <Table.Tr
                    style={{
                      backgroundColor: "var(--mantine-color-default-hover)",
                      ...(view.id === highlightedId ? HIGHLIGHT_STYLE : {}),
                    }}
                  >
                    <Table.Td colSpan={COLUMN_COUNT}>
                      <Group justify="space-between" align="center" gap="md" wrap="nowrap">
                        <Box style={{ minWidth: 0 }}>
                          <Text size="lg" fw={600} lineClamp={1}>
                            {personName(person)}
                          </Text>
                          {view.contact ? (
                            <Text size="md" c="dimmed" lineClamp={1}>
                              {view.contact}
                            </Text>
                          ) : null}
                        </Box>
                        <Group gap="md" align="center" wrap="nowrap">
                          <PersonFigures person={person} money={money} />
                          {view.markPaidButton}
                        </Group>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                  {person.shifts.map((shift) => (
                    <ShiftRow key={shift.assignmentId} shift={shift} money={money} eventTimezone={eventTimezone} />
                  ))}
                  {view.payments.length > 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={COLUMN_COUNT}>
                        <Stack gap={6}>{view.payments.map((payment) => paymentLine(person, payment))}</Stack>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  }

  // ── Tablet / phone: a card per person ──
  return (
    <Stack gap="md">
      {people.map((person) => {
        const view = viewOf(person);
        const id = view.id;

        const swipeActions: SwipeAction[] = view.canMarkPaid
          ? [
              {
                key: "markPaid",
                icon: "Cash",
                label: t("volunteers:staffPay.actions.markPaid", { defaultValue: "Mark paid" }),
                color: "teal",
                onClick: () => onMarkPaid?.(person),
                secondary: true,
              },
            ]
          : [];

        const card = (showButtons: boolean) => (
          <Paper
            p="md"
            withBorder
            radius="md"
            style={{ transition: "box-shadow 150ms ease", ...(id === highlightedId ? HIGHLIGHT_STYLE : {}) }}
          >
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start" gap="md">
                <Box style={{ minWidth: 0, flex: "1 1 220px" }}>
                  <Text size="lg" fw={600} lineClamp={1}>
                    {personName(person)}
                  </Text>
                  {view.contact ? (
                    <Text size="md" c="dimmed" lineClamp={1}>
                      {view.contact}
                    </Text>
                  ) : null}
                </Box>
                <Group gap="md" align="center" wrap="wrap" justify="flex-end">
                  <PersonFigures person={person} money={money} />
                  {showButtons ? view.markPaidButton : null}
                </Group>
              </Group>

              <ShiftsStack shifts={person.shifts} money={money} eventTimezone={eventTimezone} />

              {view.payments.length > 0 ? <Stack gap={6}>{view.payments.map((payment) => paymentLine(person, payment))}</Stack> : null}
            </Stack>
          </Paper>
        );

        return (
          <React.Fragment key={id}>
            {/* Phones: swipe right → "Mark paid" (a do-something action). */}
            <Box hiddenFrom="sm">
              {swipeActions.length > 0 ? (
                <SwipeableCard
                  leftSwipeActions={swipeActions}
                  forceClose={shouldForceClose(id)}
                  onForceCloseComplete={() => handleForceCloseComplete(id)}
                  onSwipeStateChange={(isOpen) => {
                    if (isOpen) {
                      openCard(id);
                    }
                  }}
                  borderRadius="var(--mantine-radius-md)"
                >
                  {card(false)}
                </SwipeableCard>
              ) : (
                card(false)
              )}
            </Box>
            <Box visibleFrom="sm">{card(true)}</Box>
          </React.Fragment>
        );
      })}
    </Stack>
  );
};

export const StaffPayPeopleList = React.memo(StaffPayPeopleListComponent);
